'use client';

import {
  useContext,
  useLayoutEffect,
  useRef,
  type ComponentProps,
  type CSSProperties,
  type ElementType,
} from 'react';

import { TransitionGroupContext } from '#components/TransitionGroup';
import type { AnimationStyle } from '#types';
import { removeStyle, setStyle } from '#utils';

type CSSTransitionProps<T extends ElementType> = {
  as?: T;
  initial: AnimationStyle;
  animate: AnimationStyle;
  exit: AnimationStyle;
  duration: number;
  easing?: CSSProperties['animationTimingFunction'];
} & Omit<ComponentProps<T>, 'as'>;

export const CSSTransition = <T extends ElementType>({
  children,
  as,
  initial,
  animate,
  exit,
  duration,
  easing = 'ease',
  style,
  ...props
}: CSSTransitionProps<T>) => {
  const Component = as || 'div';
  const elementRef = useRef<HTMLDivElement>(null);
  const context = useContext(TransitionGroupContext);

  if (context === undefined) {
    throw new Error('CSSTransition must be used within an TransitionGroup');
  }

  const { isUnmounted, key, requestUnmount } = context;

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    element.style.transition = `all ${duration}ms ${easing}`;

    void element.offsetTop;
    setStyle(element, initial);
    void element.offsetTop;

    removeStyle(element, initial);
    setStyle(element, animate);

    // eslint-disable-next-line
  }, []);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!isUnmounted || !element) {
      return;
    }

    const handleTransitionEnd = (e: TransitionEvent) => {
      const element = elementRef.current;
      if (!element || e.target !== element) {
        return;
      }

      requestUnmount(key);
    };

    element.addEventListener('transitionend', handleTransitionEnd);

    removeStyle(element, initial);
    removeStyle(element, animate);
    element.style.pointerEvents = 'none';
    setStyle(element, exit);

    return () => {
      element.removeEventListener('transitionend', handleTransitionEnd);
    };
    // eslint-disable-next-line
  }, [isUnmounted, key, requestUnmount]);

  return (
    <Component
      ref={elementRef}
      style={{ ...(initial as CSSProperties), ...style }}
      {...props}
    >
      {children}
    </Component>
  );
};
